// Pure helpers for the app force / recommend update card (asrth/mdf-app#205,
// T-017 Part A) on StorefrontSettings.vue. Behaviour is pinned in
// tests/unit/appVersionSettings.spec.js (.vue cannot be mounted under this
// repo's jest, so the logic lives here).
//
// API: GET / PUT /api/v1/bo/settings/app-version (mdf-bo-api
// controllers/App_version_policy.go). PUT is admin-only; every leaf is
// optional, "" clears the value (row deleted), a missing leaf is untouched.
//
// The rules below MIRROR the API's validation so the operator gets the message
// next to the field; the API stays the referee.

export const APP_VERSION_PLATFORMS = ["android", "ios"];

export const APP_UPDATE_MESSAGE_MAX = 300;

// Placeholder only — the API serves this when the Android store URL is empty.
export const APP_STORE_URL_ANDROID_DEFAULT = "https://play.google.com/store/apps/details?id=com.madamefin.app";
export const APP_STORE_URL_IOS_PLACEHOLDER = "https://apps.apple.com/app/id…";

// Stored version: 1–3 numeric segments, no "v", no "+build" (API appVersionFormatRe).
const VERSION_RE = /^\d+(\.\d+){0,2}$/;

function str(v) {
    return typeof v === "string" ? v.trim() : "";
}

// isAppVersion — true for "1", "1.5", "1.5.0"; false for "", "v1.5.0", "1.5.0+3", "1.5.0.1".
export function isAppVersion(v) {
    return VERSION_RE.test(str(v));
}

// compareAppVersion mirrors the API's compareAppVersion: numeric per segment,
// missing segment = 0, leading v stripped, +build dropped. -1 / 0 / 1.
export function compareAppVersion(a, b) {
    const norm = (s) => {
        let t = str(s).replace(/^[vV]/, "");
        const plus = t.indexOf("+");
        if (plus >= 0) t = t.slice(0, plus);
        return t.split(".");
    };
    const seg = (parts, i) => {
        if (i >= parts.length) return 0;
        const n = parseInt(parts[i], 10);
        return Number.isFinite(n) && /^\s*\d+\s*$/.test(parts[i]) ? n : 0;
    };
    const pa = norm(a);
    const pb = norm(b);
    const n = Math.max(pa.length, pb.length);
    for (let i = 0; i < n; i++) {
        const x = seg(pa, i);
        const y = seg(pb, i);
        if (x < y) return -1;
        if (x > y) return 1;
    }
    return 0;
}

export function isHttpsStoreUrl(v) {
    const s = str(v);
    if (!s.startsWith("https://")) return false;
    try {
        const u = new URL(s);
        return u.protocol === "https:" && u.hostname !== "";
    } catch (e) {
        return false;
    }
}

export function emptyAppVersionForm() {
    return {
        android: { min_version: "", latest_version: "", store_url: "" },
        ios: { min_version: "", latest_version: "", store_url: "" },
        message_th: "",
        message_en: ""
    };
}

// normalizeAppVersionResponse — GET `data` → form shape; tolerant of missing keys.
export function normalizeAppVersionResponse(data) {
    const d = data && typeof data === "object" ? data : {};
    const form = emptyAppVersionForm();
    APP_VERSION_PLATFORMS.forEach((p) => {
        const src = d[p] && typeof d[p] === "object" ? d[p] : {};
        form[p] = {
            min_version: str(src.min_version),
            latest_version: str(src.latest_version),
            store_url: str(src.store_url)
        };
    });
    form.message_th = typeof d.message_th === "string" ? d.message_th : "";
    form.message_en = typeof d.message_en === "string" ? d.message_en : "";
    return form;
}

// validateAppVersionForm → {} when valid, else { "<platform>.<field>" | "message_th" | "message_en": errKey }
// errKey ∈ invalid_version | min_gt_latest | store_url_https | message_too_long
export function validateAppVersionForm(form) {
    const errors = {};
    const f = form || emptyAppVersionForm();
    APP_VERSION_PLATFORMS.forEach((p) => {
        const pf = f[p] || {};
        const min = str(pf.min_version);
        const latest = str(pf.latest_version);
        const url = str(pf.store_url);
        if (min !== "" && !isAppVersion(min)) errors[p + ".min_version"] = "invalid_version";
        if (latest !== "" && !isAppVersion(latest)) errors[p + ".latest_version"] = "invalid_version";
        if (!errors[p + ".min_version"] && !errors[p + ".latest_version"] && min !== "" && latest !== "" &&
            compareAppVersion(min, latest) > 0) {
            errors[p + ".min_version"] = "min_gt_latest";
        }
        if (url !== "" && !isHttpsStoreUrl(url)) errors[p + ".store_url"] = "store_url_https";
    });
    ["message_th", "message_en"].forEach((k) => {
        const v = typeof f[k] === "string" ? f[k].trim() : "";
        if (Array.from(v).length > APP_UPDATE_MESSAGE_MAX) errors[k] = "message_too_long";
    });
    return errors;
}

// raisedMinVersions — platforms whose min_version goes UP with this save
// (unset → set counts as raising). Each item: { platform, version }.
// Drives the "customers below X will be locked out" confirm.
export function raisedMinVersions(saved, form) {
    const s = saved || emptyAppVersionForm();
    const f = form || emptyAppVersionForm();
    const out = [];
    APP_VERSION_PLATFORMS.forEach((p) => {
        const before = str((s[p] || {}).min_version);
        const after = str((f[p] || {}).min_version);
        if (after === "") return;
        if (before === "" || compareAppVersion(after, before) > 0) {
            out.push({ platform: p, version: after });
        }
    });
    return out;
}

// raisedMinLabel — "Android 1.5.0, iOS 1.5.0" for the confirm text.
export function raisedMinLabel(raised) {
    const names = { android: "Android", ios: "iOS" };
    return (raised || []).map((r) => names[r.platform] + " " + r.version).join(", ");
}

// appVersionPayload — full PUT body, trimmed. Every leaf sent: "" clears.
export function appVersionPayload(form) {
    const f = form || emptyAppVersionForm();
    const body = {};
    APP_VERSION_PLATFORMS.forEach((p) => {
        const pf = f[p] || {};
        body[p] = {
            min_version: str(pf.min_version),
            latest_version: str(pf.latest_version),
            store_url: str(pf.store_url)
        };
    });
    body.message_th = str(f.message_th);
    body.message_en = str(f.message_en);
    return body;
}

// canEditAppVersion / canSaveAppVersion — verifier S1 (T-017). The PUT sends
// EVERY leaf and "" deletes a row, so saving a form that never loaded (GET
// failed ⇒ all boxes empty) would silently wipe the whole policy. Editing and
// saving therefore require a successful load in this page visit.
//   state: { isAdmin, loaded, loading, saving }
export function canEditAppVersion(state) {
    const s = state || {};
    return s.isAdmin === true && s.loaded === true && s.loading !== true;
}

export function canSaveAppVersion(state) {
    const s = state || {};
    return canEditAppVersion(s) && s.saving !== true;
}
